import React, { useState, useEffect, useMemo, forwardRef } from 'react';
import { Resizable } from 'react-resizable';

/**
 * Hook helper para manejar columnas redimensionables
 * @param {Array} originColumns - Columnas originales
 * @param {Object} columnConstraints - Restricciones de tamaño por columna
 * @param {Array} dependencies - Dependencias para actualizar columnas
 */
export const useResizableColumns = (originColumns, columnConstraints = {}, dependencies = []) => {
  const [columns, setColumns] = useState([]);

  // Helper para manejar resize
  const handleResize = (index) => (e, { size }) => {
    setColumns((prevColumns) => {
      const nextColumns = [...prevColumns];
      nextColumns[index] = {
        ...nextColumns[index],
        width: size.width,
      };
      return nextColumns;
    });
  };

  // Actualizar columnas cuando cambien las dependencias
  useEffect(() => {
    setColumns(
      originColumns.map((column, index) => {
        if (column.width) {
          const constraints = columnConstraints[column.key] || {};
          return {
            ...column,
            onHeaderCell: (col) => ({
              width: col.width,
              minWidth: constraints.minWidth,
              maxWidth: constraints.maxWidth,
              onResize: handleResize(index),
            }),
          };
        }
        return column;
      })
    );
  }, dependencies);

  return columns;
};

/**
 * Hook para manejar filas expandidas con búsqueda
 * @param {string} searchValue - Valor de búsqueda
 * @param {Array} expandKeys - Keys a expandir automáticamente
 */
export const useExpandedRows = (searchValue, expandKeys = []) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [manuallyExpanded, setManuallyExpanded] = useState(new Set());

  useEffect(() => {
    if (searchValue) {
      // Cuando hay búsqueda, expandir SOLO las filas que coinciden con la búsqueda actual
      // NO mantener las expandidas de búsquedas anteriores
      setExpandedRowKeys(expandKeys);
    } else {
      // Si no hay búsqueda, mantener solo las expandidas manualmente
      setExpandedRowKeys(Array.from(manuallyExpanded));
    }
  }, [searchValue, expandKeys.join(',')]); // Usar join para evitar comparación de referencia

  // Wrapper para setExpandedRowKeys que rastrea expansiones manuales
  const handleSetExpandedRowKeys = (keys) => {
    if (typeof keys === 'function') {
      setExpandedRowKeys((prevKeys) => {
        const newKeys = keys(prevKeys);
        // Solo actualizar manuallyExpanded si NO hay búsqueda activa
        // (las expansiones durante búsqueda son automáticas, no manuales)
        if (!searchValue) {
          setManuallyExpanded(prev => new Set(newKeys));
        }
        return newKeys;
      });
    } else {
      setExpandedRowKeys(keys);
      // Solo actualizar manuallyExpanded si NO hay búsqueda activa
      if (!searchValue) {
        setManuallyExpanded(prev => new Set(keys));
      }
    }
  };

  return [expandedRowKeys, handleSetExpandedRowKeys];
};

/**
 * Helper para normalizar texto en búsquedas
 * @param {string} text - Texto a normalizar
 */
export const normalizeText = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Quitar acentos
    .replace(/[^a-z0-9\s]/g, ''); // Quitar caracteres especiales
};

/**
 * Helper para resaltar coincidencias en texto
 * @param {string} text - Texto donde buscar
 * @param {string} search - Término de búsqueda
 */
export const highlightMatch = (text, search) => {
  if (!search || !text) return text;

  const normalizedText = normalizeText(text);
  const normalizedSearch = normalizeText(search);

  if (!normalizedText.includes(normalizedSearch)) {
    return text;
  }

  // Encontrar la posición de la coincidencia
  const index = normalizedText.indexOf(normalizedSearch);
  const length = search.length;

  // Aplicar el resaltado
  return (
    <span>
      {text.substring(0, index)}
      <span style={{ backgroundColor: '#ffd666', fontWeight: 'bold' }}>
        {text.substring(index, index + length)}
      </span>
      {text.substring(index + length)}
    </span>
  );
};

/**
 * Funciones helper para sorters que manejan valores NULL correctamente
 */

/**
 * Crea un sorter para strings que pone los NULL al final
 * @param {string|function} fieldOrAccessor - Campo a ordenar o función para obtener el valor
 * @returns {function} Función sorter para Arco Table
 */
export const createStringSorter = (fieldOrAccessor) => {
  return (a, b) => {
    // Si es una función, úsala para obtener el valor, si no, obtén el campo directamente
    const aVal = typeof fieldOrAccessor === 'function'
      ? fieldOrAccessor(a)
      : a[fieldOrAccessor] || '';
    const bVal = typeof fieldOrAccessor === 'function'
      ? fieldOrAccessor(b)
      : b[fieldOrAccessor] || '';

    // Si ambos están vacíos, son iguales
    if (!aVal && !bVal) return 0;

    // Si solo a está vacío, va después
    if (!aVal) return 1;

    // Si solo b está vacío, va después
    if (!bVal) return -1;

    // Si ambos tienen valor, ordenar alfabéticamente
    return aVal.localeCompare(bVal, 'es');
  };
};

/**
 * Crea un sorter para números que pone los NULL al final
 * @param {string|function} fieldOrAccessor - Campo a ordenar o función para obtener el valor
 * @returns {function} Función sorter para Arco Table
 */
export const createNumberSorter = (fieldOrAccessor) => {
  return (a, b) => {
    // Si es una función, úsala para obtener el valor, si no, obtén el campo directamente
    const aVal = typeof fieldOrAccessor === 'function'
      ? fieldOrAccessor(a)
      : a[fieldOrAccessor];
    const bVal = typeof fieldOrAccessor === 'function'
      ? fieldOrAccessor(b)
      : b[fieldOrAccessor];

    // Si ambos son null/undefined/0, son iguales
    if ((aVal === null || aVal === undefined || aVal === 0) &&
        (bVal === null || bVal === undefined || bVal === 0)) return 0;

    // Si solo a es null/undefined, va después
    if (aVal === null || aVal === undefined) return 1;

    // Si solo b es null/undefined, va después
    if (bVal === null || bVal === undefined) return -1;

    // Si ambos tienen valor, ordenar numéricamente
    return aVal - bVal;
  };
};

/**
 * Crea un sorter para fechas que pone los NULL al final
 * @param {string|function} fieldOrAccessor - Campo a ordenar o función para obtener el valor
 * @returns {function} Función sorter para Arco Table
 */
export const createDateSorter = (fieldOrAccessor) => {
  return (a, b) => {
    // Si es una función, úsala para obtener el valor, si no, obtén el campo directamente
    const aVal = typeof fieldOrAccessor === 'function'
      ? fieldOrAccessor(a)
      : a[fieldOrAccessor];
    const bVal = typeof fieldOrAccessor === 'function'
      ? fieldOrAccessor(b)
      : b[fieldOrAccessor];

    // Si ambos son null/undefined, son iguales
    if (!aVal && !bVal) return 0;

    // Si solo a es null/undefined, va después
    if (!aVal) return 1;

    // Si solo b es null/undefined, va después
    if (!bVal) return -1;

    // Si ambos tienen valor, ordenar por fecha
    return new Date(aVal).getTime() - new Date(bVal).getTime();
  };
};

/**
 * Configuración de restricciones comunes para columnas
 */
export const commonColumnConstraints = {
  id: { minWidth: 60, maxWidth: 100 },
  nombre: { minWidth: 150, maxWidth: 400 },
  name: { minWidth: 150, maxWidth: 400 },
  tipo: { minWidth: 80, maxWidth: 150 },
  type: { minWidth: 80, maxWidth: 150 },
  nivel: { minWidth: 100, maxWidth: 200 },
  level: { minWidth: 100, maxWidth: 200 },
  acciones: { minWidth: 100, maxWidth: 200 },
  actions: { minWidth: 100, maxWidth: 200 },
  fecha: { minWidth: 100, maxWidth: 180 },
  date: { minWidth: 100, maxWidth: 180 },
  estado: { minWidth: 80, maxWidth: 150 },
  status: { minWidth: 80, maxWidth: 150 },
  descripcion: { minWidth: 200, maxWidth: 500 },
  description: { minWidth: 200, maxWidth: 500 }
};

/**
 * Handle personalizado para redimensionar columnas
 */
export const CustomResizeHandle = forwardRef((props, ref) => {
  const { handleAxis, ...restProps } = props;
  return (
    <span
      ref={ref}
      className={`react-resizable-handle react-resizable-handle-${handleAxis}`}
      {...restProps}
      onClick={(e) => {
        e.stopPropagation();
      }}
    />
  );
});

/**
 * Componente de título de columna redimensionable
 * Usado por todas las tablas del módulo
 */
export const ResizableTitle = (props) => {
  const { onResize, width, minWidth, maxWidth, ...restProps } = props;

  // Si la columna no tiene width definido, renderizar th normal
  if (!width) {
    return <th {...restProps} />;
  }

  // Si la columna tiene className que indica que es fija (fixed), NO usar Resizable
  // Arco Design añade clases como 'arco-table-col-fixed-left' o 'arco-table-col-fixed-right'
  const isFixed = restProps.className &&
    (restProps.className.includes('arco-table-col-fixed') ||
     restProps.className.includes('fixed'));

  if (isFixed) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      minConstraints={[minWidth || 60, 0]}
      maxConstraints={[maxWidth || 600, 0]}
      handle={<CustomResizeHandle />}
      onResize={onResize}
      draggableOpts={{
        enableUserSelectHack: false,
      }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

/**
 * Helper para manejar el resize de columnas
 */
export const handleResize = (index, setColumns) => (e, { size }) => {
  setColumns((prevColumns) => {
    const nextColumns = [...prevColumns];
    nextColumns[index] = {
      ...nextColumns[index],
      width: size.width,
    };
    return nextColumns;
  });
};

/**
 * Configuración de componentes para tabla con columnas redimensionables
 */
export const resizableComponents = {
  header: {
    th: ResizableTitle,
  },
};

export default {
  useResizableColumns,
  useExpandedRows,
  normalizeText,
  highlightMatch,
  commonColumnConstraints,
  CustomResizeHandle,
  ResizableTitle,
  handleResize,
  resizableComponents
};